import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Calendar, MessageCircle, Users, Video, PhoneCall, Globe, Smartphone, Building } from "lucide-react";

const GoogleHangoutsHistory = () => {
  const timelineEvents = [
    {
      year: "2013",
      title: "Google Hangouts Launched",
      description: "Google launched Hangouts at Google I/O 2013, replacing Google Talk, Google+ Messenger, and the original Hangouts video chat feature from Google+. It unified messaging across Google's ecosystem.",
      icon: <Video className="h-6 w-6" />
    },
    {
      year: "2014",
      title: "SMS Integration Added",
      description: "Hangouts gained the ability to send and receive SMS messages on Android, positioning it as a unified messaging hub for all communication types.",
      icon: <Smartphone className="h-6 w-6" />
    },
    {
      year: "2015",
      title: "Google Fi Integration",
      description: "Hangouts became the default messaging and calling app for Google's Project Fi (now Google Fi) wireless service, enabling calls and texts over WiFi.",
      icon: <PhoneCall className="h-6 w-6" />
    },
    {
      year: "2016",
      title: "Allo and Duo Announced",
      description: "Google announced Allo (messaging) and Duo (video calling) as new standalone apps, signaling the beginning of Hangouts' fragmentation.",
      icon: <MessageCircle className="h-6 w-6" />
    },
    {
      year: "2017",
      title: "Enterprise Focus Shift",
      description: "Google began repositioning Hangouts for enterprise use with Hangouts Chat and Hangouts Meet, while consumer features remained in classic Hangouts.",
      icon: <Building className="h-6 w-6" />
    },
    {
      year: "2019",
      title: "Migration to Chat/Meet Begins",
      description: "Google officially announced plans to migrate Hangouts users to Google Chat and Google Meet, beginning the sunset process for consumer Hangouts.",
      icon: <Globe className="h-6 w-6" />
    },
    {
      year: "2022",
      title: "Consumer Hangouts Discontinued",
      description: "Google Hangouts for consumers was fully discontinued in November 2022. All users were migrated to Google Chat and Google Meet.",
      icon: <Calendar className="h-6 w-6" />
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The History of Google Hangouts - From Launch to Sunset",
    "description": "Explore the complete history of Google Hangouts from its 2013 launch to its 2022 shutdown, including its evolution and replacement by Google Chat and Meet.",
    "url": "https://cramstudywith.me/google-hangouts/history/",
    "datePublished": "2024-01-15",
    "dateModified": "2024-01-15",
    "author": {
      "@type": "Organization",
      "name": "CramStudyWith.me"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CramStudyWith.me",
      "url": "https://cramstudywith.me"
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>The History of Google Hangouts - From Launch to Sunset | CramStudyWith.me</title>
        <meta name="description" content="Explore the complete history of Google Hangouts from its 2013 launch to its 2022 shutdown, including its evolution and replacement by Google Chat and Meet." />
        <meta name="keywords" content="Google Hangouts history, Google Hangouts shutdown, Google Hangouts timeline, Google Chat, Google Meet, video calling history" />
        <link rel="canonical" href="https://cramstudywith.me/google-hangouts/history/" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      </Helmet>
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              The History of Google Hangouts
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              From its ambitious 2013 launch to its 2022 sunset - explore the complete story of Google's unified communication platform and why it was replaced.
            </p>
            <Link to="/google-hangouts/">
              <Button size="lg" variant="outline" className="gap-2">
                <ArrowRight className="h-5 w-5" />
                Find a Google Hangouts Alternative
              </Button>
            </Link>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">Google Hangouts Timeline</h2>
              <div className="space-y-8">
                {timelineEvents.map((event, index) => (
                  <Card key={index} className="relative overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-secondary" />
                    <CardHeader className="flex flex-row items-center gap-4">
                      <div className="p-3 rounded-full bg-primary/10 text-primary">
                        {event.icon}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-primary">{event.year}</div>
                        <CardTitle className="text-xl">{event.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{event.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What Made Hangouts Special Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center">What Made Google Hangouts Special</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-primary" />
                      Unified Communication
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Hangouts unified text, voice, and video communication in one app, syncing seamlessly across Android, iOS, and web platforms.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      Group Video Calls
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Free group video calls for up to 10 participants made Hangouts popular for remote meetings, study groups, and family gatherings.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Video className="h-5 w-5 text-primary" />
                      Hangouts On Air
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      The ability to broadcast live to YouTube made Hangouts a pioneer in live streaming before dedicated platforms became mainstream.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Smartphone className="h-5 w-5 text-primary" />
                      Google Integration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Deep integration with Gmail, Google Calendar, and Chrome made Hangouts easily accessible wherever users were in Google's ecosystem.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Legacy Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">Google Hangouts' Legacy</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Despite its discontinuation, Google Hangouts played a crucial role in making video calling mainstream. It demonstrated that high-quality video communication could be free and accessible to everyone.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                For users seeking video collaboration tools for studying today, <Link to="/google-hangouts/" className="text-primary hover:underline">CramStudyWith.me offers purpose-built alternatives</Link> with video study rooms, productivity tools, and features designed specifically for educational success.
              </p>
              <Link to="/google-hangouts/">
                <Button size="lg" className="gap-2">
                  Explore Google Hangouts Alternatives
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GoogleHangoutsHistory;